import { useEffect, useState } from "react";
import { toast } from "sonner";
import { PackageCheck, Pencil } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, errMsg } from "@/lib/api";
import { FormPicker, useActiveForms } from "@/components/DefaultPackets";

const SRC = { provider: "provider's own default", facility: "facility default", organization: "organization default" };

export function useResolvedDefault(facility, provider, bump = 0) {
  const [r, setR] = useState(null);
  useEffect(() => {
    api.get("/default-packets/resolve", { params: { facility: facility || "", provider: provider || "" } }).then((x) => setR(x.data)).catch(() => setR(null));
  }, [facility, provider, bump]);
  return r;
}

export function DefaultPacketPreview({ facility, provider, bump, testid = "default-packet-preview", children }) {
  const r = useResolvedDefault(facility, provider, bump);
  if (!r) return null;
  return (
    <div className="flex flex-wrap items-start gap-2 rounded-lg border border-sky-200 bg-sky-50/70 p-2.5 text-sm" data-testid={testid}>
      <PackageCheck className="w-4 h-4 mt-0.5 text-sky-700 shrink-0" />
      <div className="flex-1 min-w-[180px]">
        <p className="font-medium text-slate-800">Default packet for new patients</p>
        {r.scope ? <p className="text-slate-600"><span data-testid={`${testid}-names`}>{r.names.join(", ")}</span> <span className="text-xs text-sky-800" data-testid={`${testid}-source`}>({SRC[r.scope]})</span></p>
          : <p className="text-slate-500" data-testid={`${testid}-source`}>None set - staff pick forms by hand.</p>}
      </div>
      {children}
    </div>
  );
}

export function MyDefaultPacketButton({ onSaved }) {
  const [open, setOpen] = useState(false);
  const [v, setV] = useState([]);
  const forms = useActiveForms(open);
  useEffect(() => { if (open) api.get("/default-packets/mine").then((r) => setV(r.data.family_ids)).catch((e) => toast.error(errMsg(e))); }, [open]);
  const save = async (ids) => {
    try { await api.put("/default-packets/mine", { family_ids: ids }); toast.success(ids.length ? "Your default packet is saved" : "Using the facility default again"); setOpen(false); onSaved?.(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <>
      <Button size="sm" variant="outline" onClick={() => setOpen(true)} data-testid="my-default-packet-btn"><Pencil className="w-3.5 h-3.5 mr-1" />My default packet</Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-xl" data-testid="my-default-packet-dialog">
          <DialogHeader><DialogTitle>My default packet</DialogTitle>
            <DialogDescription>Used for your new patients instead of the facility default. Leave everything unticked to use the facility default.</DialogDescription></DialogHeader>
          <FormPicker forms={forms} value={v} onChange={setV} testid="my-default-forms" />
          <div className="flex justify-end gap-2 mt-2">
            <Button variant="outline" onClick={() => save([])} data-testid="my-default-clear">Use facility default</Button>
            <Button className="btn-brand" onClick={() => save(v)} data-testid="my-default-save">Save</Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
